import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import DoneIcon from '@mui/icons-material/Done';
import Autocomplete, { autocompleteClasses, createFilterOptions } from '@mui/material/Autocomplete';
import ButtonBase from '@mui/material/ButtonBase';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useEffect, useState, useMemo } from 'react';
import { ArrowDropDown } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import EllipsisTypography from '../EllipsisTypography/EllipsisTypography';
import NRAutocompleteStyles from './NRAutocomplete.styles';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import AssetAndGroupsButtons from '../NRAISearch/NRAISearchFilters/NRAutocomplete/AssetAndGroupsButtons';
import NRVirtualizedListBox from '../NRVirtualizedLists/NRVirtualizedListBox';
const useStyles = makeStyles(NRAutocompleteStyles);
const filter = createFilterOptions();

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.background.paper,
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      backgroundColor: theme.palette.background.paper,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent'
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
        backgroundColor: theme.palette.action.hover
      }
    }
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative'
  }
}));

function PopperComponent(props) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 8px 24px ${theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'}`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: '10px 16px',
  width: '100%',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& input': {
    borderRadius: 4,
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.divider}`,
    fontSize: 14
  }
}));

const Button = styled(ButtonBase)(({ theme }) => ({
  fontSize: 13,
  width: '100%',
  textAlign: 'left',
  paddingBottom: 8,
  color: theme.palette.text.icon,
  fontWeight: 600,
  '& span': {
    width: '100%'
  },
  '& svg': {
    width: 16,
    height: 16
  }
}));

function NRAutocomplete({
  arrayData = [],
  value = [],
  setValue,
  loading,
  fullWidth = false,
  title,
  label,
  dataNameSingular = 'Group',
  fieldToShow = 'name',
  idField = 'id',
  multiple = true,
  disabled = false,
  showAlwaysTitle = false,
  assetsAndGroups = false,
  onOpenChange = () => {},
  helperText = '',
  color
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingValue, setPendingValue] = useState(multiple ? (Array.isArray(value) ? value : []) : value?.[0] || null);
  const theme = useTheme();
  const classes = useStyles();

  const handleClick = event => {
    setPendingValue(multiple ? (Array.isArray(value) ? value : []) : value?.[0] || null);
    setAnchorEl(event.currentTarget);
    onOpenChange(true);
  };

  const handleClose = finalValue => {
    onOpenChange(false);
    const valueToUse = finalValue !== undefined ? finalValue : pendingValue;
    const valueToCommit = multiple ? [...(Array.isArray(valueToUse) ? valueToUse : [])] : valueToUse ? [valueToUse] : [];
    setValue(valueToCommit);
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    if (assetsAndGroups) {
      setPendingValue(multiple ? (Array.isArray(value) ? value : []) : value?.[0] || null);
    }
  }, [value, arrayData, multiple, assetsAndGroups]);

  const open = Boolean(anchorEl);
  const id = open ? `${dataNameSingular}s-filter` : undefined;

  const handleOnClear = e => {
    e?.stopPropagation();
    setPendingValue(multiple ? [] : null);
    setValue([]);
    onOpenChange(false);
  };

  const sortedOptions = useMemo(() => {
    const topOptionId = '';
    const selectedIds = new Set(
      (multiple ? (Array.isArray(value) ? value : []) : value?.[0] ? [value[0]] : []).map(v => (v && typeof v === 'object' ? v[idField] : v))
    );

    const topOptions = [];
    const selectedOptions = [];
    const otherOptions = [];
    const seenIds = new Set();

    arrayData.forEach(item => {
      const currentId = item[idField];
      if (seenIds.has(currentId)) return;
      seenIds.add(currentId);

      if (currentId === topOptionId) {
        topOptions.push(item);
      } else if (selectedIds.has(currentId)) {
        selectedOptions.push(item);
      } else {
        otherOptions.push(item);
      }
    });

    const sortByField = (a, b) => String(a[fieldToShow] || '').localeCompare(String(b[fieldToShow] || ''));
    selectedOptions.sort(sortByField);
    otherOptions.sort(sortByField);

    return [...topOptions, ...selectedOptions, ...otherOptions];
  }, [arrayData, value, idField, multiple, fieldToShow]);

  const displayValue = useMemo(() => {
    if (!arrayData?.length) {
      if (loading) return `Loading ${dataNameSingular}s...`;
      return '';
    }
    if (value?.length === 1) {
      if (multiple) {
        return `1 ${dataNameSingular} selected`;
      }
      return typeof value[0] === 'object' && value[0] !== null
        ? value[0][fieldToShow]
        : arrayData.find(e => e[idField] === value[0])?.[fieldToShow] || value[0];
    } else if (value?.length > 1) {
      return `${value.length} ${dataNameSingular}s selected`;
    }
    return '';
  }, [value, arrayData, fieldToShow, idField, dataNameSingular, loading]);

  return (
    <React.Fragment>
      <Box
        sx={{
          width: fullWidth ? '100%' : 'fit-content',
          minWidth: fullWidth ? 'unset' : theme.spacing(29),
          fontSize: 16,
          ...(disabled ? { pointerEvents: 'none', opacity: 0.5 } : {})
        }}
      >
        {label ? (
          <TextField
            label={label}
            value={displayValue}
            onClick={handleClick}
            fullWidth={fullWidth}
            disabled={disabled || (!arrayData?.length && !loading)}
            helperText={helperText}
            variant="outlined"
            color={color}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {!!value?.length && (
                    <IconButton size="small" onClick={handleOnClear} sx={{ mr: -0.5 }}>
                      <ClearIcon />
                    </IconButton>
                  )}
                  <ArrowDropDown sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: fullWidth ? '100%' : 'fit-content',
              minWidth: fullWidth ? 'unset' : theme.spacing(29),
              '& .MuiInputBase-root': {
                cursor: 'pointer'
              },
              '& .MuiInputBase-input': {
                cursor: 'pointer',
                textOverflow: 'ellipsis'
              }
            }}
          />
        ) : (
          <React.Fragment>
            <Button
              aria-describedby={id}
              onClick={handleClick}
              className={classes.selectButton}
              disabled={disabled || !arrayData?.length}
              disableTouchRipple
            >
              {!!showAlwaysTitle && !!title && <>{title}</>}
              {!showAlwaysTitle && !arrayData?.length && !!loading && <Typography variant={'body1'} children={`Loading ${dataNameSingular}s...`} />}
              {!showAlwaysTitle && !arrayData?.length && !loading && <Typography variant={'body1'} children={`No ${dataNameSingular}s Present`} />}
              {!showAlwaysTitle && !!arrayData?.length && (
                <EllipsisTypography variant={'body1'} color="text.primary" sx={{ flexGrow: 1, textAlign: 'left' }}>
                  {displayValue || title || `${dataNameSingular}s`}
                </EllipsisTypography>
              )}
              {!!value?.length && (
                <IconButton size="small" sx={{ marginLeft: 'auto' }}>
                  <ClearIcon onClick={handleOnClear} />
                </IconButton>
              )}
              <ArrowDropDown style={{ width: 24, height: 24, marginLeft: !!value?.length ? 1 : 'auto' }} />
            </Button>
            {!!helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {helperText}
              </Typography>
            )}
          </React.Fragment>
        )}
      </Box>
      {!!arrayData.length && (
        <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start">
          <ClickAwayListener onClickAway={() => handleClose()}>
            <div>
              {!!assetsAndGroups && <AssetAndGroupsButtons />}
              <Autocomplete
                open
                fullWidth={fullWidth}
                multiple={multiple}
                onClose={(event, reason) => {
                  if (reason === 'escape') {
                    handleClose();
                  }
                }}
                value={pendingValue}
                onChange={(event, newValue, reason) => {
                  if (multiple) {
                    if (newValue?.find(e => e.id === '')) {
                      setPendingValue([]);
                      setValue([]);
                      return;
                    }
                    if (event.type === 'keydown' && event?.key === 'Backspace' && reason === 'removeOption') {
                      return;
                    }

                    // Create a map to count occurrences of each id
                    const idCounts = newValue.reduce((acc, item) => {
                      acc[item[idField]] = (acc[item[idField]] || 0) + 1;
                      return acc;
                    }, {});

                    // Filter out items that have duplicate ids
                    const filteredValue = newValue.filter(item => idCounts[item[idField]] === 1);

                    setPendingValue(filteredValue);
                  } else {
                    const commitValue = newValue || null;
                    setPendingValue(commitValue);
                    handleClose(commitValue);
                  }
                }}
                disableCloseOnSelect
                disableListWrap
                ListboxComponent={NRVirtualizedListBox}
                isOptionEqualToValue={(option, val) => {
                  const optionId = option?.[idField];
                  const valId = val && typeof val === 'object' ? val[idField] : val;
                  return optionId === valId;
                }}
                filterOptions={(options, params) => filter(options, params)}
                PopperComponent={PopperComponent}
                renderTags={() => null}
                noOptionsText="No options"
                renderOption={(props, option) => {
                  // Manually check if the option is selected
                  const isSelected = multiple
                    ? Array.isArray(pendingValue) &&
                      pendingValue.some(selectedOption => {
                        const selectedId = selectedOption && typeof selectedOption === 'object' ? selectedOption[idField] : selectedOption;
                        return selectedId === option[idField];
                      })
                    : (pendingValue && typeof pendingValue === 'object' ? pendingValue[idField] : pendingValue) === option[idField];

                  return (
                    <Box
                      {...props}
                      component="li"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor:
                          multiple && pendingValue?.length === 0 && option[idField] === '' ? theme.palette.selectedBackground.selector : 'unset',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      <Box
                        component={DoneIcon}
                        className={classes.check}
                        style={{
                          visibility: isSelected ? 'visible' : 'hidden'
                        }}
                      />
                      <Box className={classes.title}>
                        <EllipsisTypography variant={'body1'} color="text.primary">
                          {option[fieldToShow]}
                        </EllipsisTypography>
                      </Box>
                      {!option[idField] && <Divider />}
                    </Box>
                  );
                }}
                options={sortedOptions}
                getOptionLabel={option => option?.[fieldToShow] || ''}
                getOptionKey={option => option?.[idField]}
                renderInput={params => (
                  <StyledInput ref={params.InputProps.ref} inputProps={params.inputProps} autoFocus placeholder={`Filter ${dataNameSingular}s`} />
                )}
              />
            </div>
          </ClickAwayListener>
        </StyledPopper>
      )}
    </React.Fragment>
  );
}

export default NRAutocomplete;
